import { useState, useEffect } from "react";
import { Elements } from "@stripe/react-stripe-js";
import { stripePromise } from "../../utils/stripeConfig";
import { SupportCheckoutForm } from "./SupportCheckoutForm";
import { Bluelagoon } from "@bluelagoon";
import { useTranslation } from "react-i18next";

export function SupportCheckoutModal({
    isOpen,
    onClose,
    selectedPlan,
    isYearly,
    isLatam,
    userId,
    paymentMethods = [],
}) {
    const { t } = useTranslation();
    const [clientSecret, setClientSecret] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen && userId && !clientSecret) {
            setLoading(true);
            setError(null);
            Bluelagoon.createSetupIntent({ userId })
                .then((response) => {
                    const secret = response?.data?.clientSecret || response?.data?.data?.clientSecret;
                    if (secret) {
                        setClientSecret(secret);
                    } else {
                        setError(t("billing.supportPlans.failedToInitialize"));
                    }
                })
                .catch((err) => setError(err.message))
                .finally(() => setLoading(false));
        }
        if (!isOpen) {
            setClientSecret(null);
            setError(null);
        }
    }, [isOpen, userId, clientSecret, t]);

    if (!isOpen || !selectedPlan) return null;

    const elementsOptions = clientSecret ? {
        clientSecret,
        appearance: { theme: "stripe", variables: { colorPrimary: "#4F46E5", borderRadius: "8px" } },
    } : undefined;

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[9999] p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4 flex justify-between items-center">
                    <h2 className="text-xl font-bold text-white">{t("billing.supportPlans.secureCheckout")}</h2>
                    <button onClick={onClose} className="text-white/80 hover:text-white text-2xl">×</button>
                </div>
                <div className="p-6 overflow-y-auto" style={{ maxHeight: "calc(90vh - 80px)" }}>
                    {loading && <LoadingSpinner t={t} />}
                    {error && <ErrorMessage error={error} t={t} />}
                    {!loading && !error && clientSecret && (
                        <Elements stripe={stripePromise} options={elementsOptions}>
                            <SupportCheckoutForm
                                selectedPlan={selectedPlan}
                                isYearly={isYearly}
                                isLatam={isLatam}
                                userId={userId}
                                paymentMethods={paymentMethods}
                                onSuccess={() => { onClose(); window.location.reload(); }}
                                onCancel={onClose}
                            />
                        </Elements>
                    )}
                </div>
            </div>
        </div>
    );
}

function LoadingSpinner({ t }) {
    return (
        <div className="flex flex-col items-center py-12">
            <div className="animate-spin h-8 w-8 border-4 border-indigo-600 border-t-transparent rounded-full mb-4" />
            <p className="text-gray-600">{t("billing.supportPlans.initializingPayment")}</p>
        </div>
    );
}

function ErrorMessage({ error, t }) {
    return (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
            <p className="text-red-700">{error}</p>
            <button onClick={() => window.location.reload()} className="text-red-600 underline mt-2 text-sm">
                {t("billing.supportPlans.tryAgain")}
            </button>
        </div>
    );
}
